import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { LOGO_URL, API_BASE } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COPIES = [
  "CONSIGNOR/CONSIGNEE COPY",
  "CONSIGNOR COPY",
  "CONSIGNEE COPY",
  "OFFICE COPY",
  "DRIVER COPY",
];

const BORDER = "1px solid #000";

export default function ConsignmentPrint() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [sig, setSig] = useState(null);
  const [copyLabel, setCopyLabel] = useState("CONSIGNOR/CONSIGNEE COPY");
  const [printAll, setPrintAll] = useState(false);

  useEffect(() => { api.get(`/consignments/${id}`).then(({ data }) => setC(data)); }, [id]);
  useEffect(() => { api.get(`/consignments/${id}/signatory`).then(({ data }) => setSig(data)).catch(() => {}); }, [id]);
  if (!c) return <div className="p-8 text-slate-500">Loading…</div>;

  const copiesToPrint = printAll
    ? ["CONSIGNOR COPY", "CONSIGNEE COPY", "OFFICE COPY", "DRIVER COPY"]
    : [copyLabel];

  return (
    <div className="p-6 bg-slate-100 min-h-screen overflow-x-auto" data-testid="consignment-print-page">
      <div className="no-print flex items-center justify-between mb-4 max-w-[297mm] mx-auto">
        <Link to="/portal/consignments" className="text-slate-600 hover:text-slate-900 text-sm inline-flex items-center gap-2">
          <ArrowLeft size={16}/> Back
        </Link>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600 flex items-center gap-2">
            <input type="checkbox" checked={printAll} onChange={(e)=>setPrintAll(e.target.checked)} data-testid="print-all-toggle"/>
            Print all 4 copies
          </label>
          {!printAll && (
            <Select value={copyLabel} onValueChange={setCopyLabel}>
              <SelectTrigger className="w-64" data-testid="copy-label-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {COPIES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <button onClick={()=>window.print()} className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="print-btn">
            <Printer size={16}/> Print / Save PDF
          </button>
        </div>
      </div>
      <div className="no-print max-w-[297mm] mx-auto mb-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded">
        <b>Tip:</b> In the browser print dialog, set <b>Layout: Landscape</b>, <b>Paper size: A4</b>, and <b>Margins: None</b>, then click Save/Print. This ensures the bilty prints on a single page with all borders.
      </div>

      <div className="cn-preview-wrap">
        {copiesToPrint.map((lbl, i) => (
          <div key={lbl} className={`print-area bg-white shadow mx-auto ${i < copiesToPrint.length - 1 ? "mb-4" : ""}`} style={{ boxSizing: "border-box" }}>
            <CN c={c} copyLabel={lbl} sig={sig} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CN({ c, copyLabel, sig }) {
  const b = BORDER;
  const cell = { borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, padding: "3px 6px", verticalAlign: "top" };
  const label = { fontWeight: 700 };
  const red = { color: "#c00", fontWeight: 700 };

  // Root: single big table with defined column widths.
  // Cols: 4 groups → [label col ~9%] [big middle ~47%] [right block ~22%] [right rail ~22%]
  // For the goods table area, we use nested layout at bottom.

  return (
    <table style={{ width: "100%", height: "100%", borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, borderCollapse: "collapse", tableLayout: "fixed", fontFamily: "'Manrope', system-ui, sans-serif", color: "#111", fontSize: 13, lineHeight: 1.18 }}>
      <colgroup>
        <col style={{ width: "9%" }}/>
        <col style={{ width: "47%" }}/>
        <col style={{ width: "22%" }}/>
        <col style={{ width: "22%" }}/>
      </colgroup>
      <tbody>

        {/* Row 1: Subject (Pitarji) | small Ganesh center | AT OWNER's RISK | COPY LABEL */}
        <tr>
          <td colSpan={3} style={{ ...cell, borderRight: 0, borderBottom: 0, padding: "3px 6px 0" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div>
                <span style={label}>Subject To Kolkata Jurisdiction</span>
                <span style={{ marginLeft: 24 }}>(Pitarji )</span>
              </div>
              <img src="/ganesha.png" alt="Shri Ganesha" style={{ height: 26, width: "auto", objectFit: "contain" }} data-testid="cn-ganesha-img" />
              <span style={{ fontWeight: 700, marginRight: 40 }}>AT OWNER's RISK</span>
            </div>
          </td>
          <td style={{ ...cell, textAlign: "center" }}>
            <span style={red}>{copyLabel}</span>
          </td>
        </tr>

        {/* Row 2: Logo | Title | TopRight rail */}
        <tr>
          <td style={{ ...cell, borderTop: 0, borderRight: 0, borderBottom: 0, textAlign: "center" }}>
            <img src={LOGO_URL} alt="JJ" style={{ width: 86, height: 86, objectFit: "contain" }}/>
          </td>
          <td colSpan={2} style={{ ...cell, borderTop: 0, borderLeft: 0, borderBottom: 0, textAlign: "center" }}>
            <div style={{ fontFamily: "'Playfair Display', serif", color: "#0b3a7a", fontWeight: 900, fontSize: 26, letterSpacing: 0.5, lineHeight: 1 }}>JAI JAGDAMBA LOGISTICS</div>
            <div>17/1, Lakshmi Narayan Mukherjee Lane, Room No.209</div>
            <div>2<sup>nd</sup> Floor, KOLKATA-700006</div>
            <div style={{ color: "#0b5cc4", textDecoration: "underline" }}>E-Mail-jjlogistics.india@gmail.com | Contcact:9831997959</div>
          </td>
          <td rowSpan={2} style={{ borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, padding: 0, verticalAlign: "top" }}>
            <TopRight c={c}/>
          </td>
        </tr>

        {/* Row 3: GSTIN | PAN (full width left) */}
        <tr>
          <td colSpan={3} style={{ ...cell, borderTop: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={label}>GSTIN:-19AAHPO6792Q1ZJ</span>
              <span style={label}>PAN: AAHPO6792Q</span>
            </div>
          </td>
        </tr>

        {/* Row 5: Disclaimer | CAUTION | From/To (right rail) */}
        <tr>
          <td colSpan={2} style={{ ...cell, fontSize: 9.5, lineHeight: 1.16, color: "#c00" }}>
            The consignment covered by this set of special lorry receipt from shall be stored at the destination under the control of the transport operator and shall be delivered to the order of the consignee bank whose name mentioned in the lorry receipt it will under no circumstance be delivered to anyone without the written authority from the consignee bank or is order endorsed on the consignee copy or one separate letter of authority,
            <div style={{ ...red, textAlign: "center", marginTop: 3 }}>NOT RESPONSIBLE FOR LEAKAGE &amp; BREAKAGE</div>
            <div style={{ marginTop: 2, textAlign: "center" }}>The party will be responsible to pay any GOVT. dues if applicable, received good as per details above for carriage subject to terms and condi ons printed overleaf, For risk &amp; Insurance details.</div>
          </td>
          <td style={{ ...cell, fontSize: 9.5, lineHeight: 1.16 }}>
            <div style={{ fontWeight: 700, textAlign: "center" }}>CAUTION</div>
            <div>The Consignment will not be detained, diverted, re-routed or re-booked without Consignee's bank written permission &amp; Consignment will be delivered at the destination.</div>
          </td>
          <td style={{ padding: 0, borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, verticalAlign: "top" }}>
            <FromTo c={c}/>
          </td>
        </tr>

        {/* Row 6: Consignor | Invoice Details */}
        <tr>
          <td style={{ ...cell, color: "#0b3a7a" }}>
            <div style={{ fontWeight: 700 }}>Consignor's</div>
            <div style={{ marginTop: 6 }}>Name and</div>
            <div style={{ marginTop: 6 }}>Address</div>
          </td>
          <td colSpan={2} style={cell}>
            <div style={{ fontWeight: 800, fontSize: 15.5 }}>{c.consignor_name}</div>
            <div style={{ whiteSpace: "pre-line", fontWeight: 700, fontSize: 15.5 }}>{c.consignor_address}</div>
            {c.consignor_pan && <div style={{ ...label, marginTop: 2, fontSize: 15.5 }}>PAN:- {c.consignor_pan}</div>}
            {c.consignor_gstin && <div style={{ ...label, marginTop: 2, fontSize: 15.5, fontWeight: 800 }}>GSTIN:- {c.consignor_gstin}</div>}
          </td>
          <td style={{ padding: 0, borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, verticalAlign: "top" }}>
            <InvEway c={c}/>
          </td>
        </tr>

        {/* Row 7: Consignee */}
        <tr>
          <td style={{ ...cell, color: "#0b3a7a" }}>
            <div style={{ fontWeight: 700 }}>Consignee's</div>
            <div style={{ marginTop: 6 }}>Name and</div>
            <div style={{ marginTop: 6 }}>Address</div>
          </td>
          <td colSpan={2} style={cell}>
            <div style={{ fontWeight: 800, fontSize: 15.5 }}>{c.consignee_name}</div>
            <div style={{ whiteSpace: "pre-line", fontWeight: 700, fontSize: 15.5 }}>{c.consignee_address}</div>
            {c.consignee_gstin && <div style={{ ...label, marginTop: 2, fontSize: 15.5, fontWeight: 800 }}>GSTIN:- {c.consignee_gstin}</div>}
          </td>
          <td style={{ padding: 0, borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, verticalAlign: "top" }}>
            <RightBottom c={c}/>
          </td>
        </tr>

        {/* Row 8: Goods table (fills remaining height) */}
        <tr style={{ height: "100%" }}>
          <td colSpan={4} style={{ padding: 0, borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, height: "100%" }}>
            <Goods c={c} sig={sig}/>
          </td>
        </tr>

        {/* Footer row: freight status (TO BE BILLED) below the To Pay/Paid section + subject note */}
        <tr>
          <td colSpan={2} style={{ ...cell, borderTop: 0, fontSize: 9, verticalAlign: "bottom" }}>Pitar ji NamahX</td>
          <td colSpan={2} style={{ ...cell, borderTop: 0, textAlign: "center", verticalAlign: "middle" }}>
            {c.freight_type && (
              <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: 1.5, textTransform: "uppercase" }}>{c.freight_type}</span>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function TopRight({ c }) {
  const b = BORDER;
  const row = { padding: "3px 6px", borderBottom: b };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={row}><span style={{ fontWeight: 700 }}>Date:- </span><span style={{ fontWeight: 700, fontSize: 15.5 }}>{formatDate(c.date)}</span></div>
      <div style={{ ...row, borderBottom: 0, flex: 1 }}><span style={{ fontWeight: 700 }}>C/N No:- </span><span style={{ fontWeight: 800, fontSize: 16.5 }}>{c.cn_no}</span></div>
    </div>
  );
}

function FromTo({ c }) {
  const b = BORDER;
  const row = { padding: "3px 6px", borderBottom: b };
  const val = { fontWeight: 700, fontSize: 15.5, wordBreak: "break-word", overflowWrap: "anywhere" };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={row}><span style={{ fontWeight: 700 }}>From:- </span><span style={val}>{c.from_location}</span></div>
      <div style={{ ...row, borderBottom: 0, flex: 1 }}><span style={{ fontWeight: 700 }}>To:- </span><span style={val}>{c.to_location}</span></div>
    </div>
  );
}

function InvEway({ c }) {
  return (
    <div style={{ padding: "3px 6px", height: "100%" }}>
      <span style={{ fontWeight: 700 }}>Inv. No:- </span>
      <span style={{ fontWeight: 700, fontSize: 15.5, wordBreak: "break-word", overflowWrap: "anywhere" }}>{c.challan_no || c.inv_no}</span>
    </div>
  );
}

function RightBottom({ c }) {
  const b = BORDER;
  const row = { padding: "3px 6px", borderBottom: b };
  const val = { fontWeight: 700, fontSize: 15.5, wordBreak: "break-word", overflowWrap: "anywhere" };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={row}><span style={{ fontWeight: 700 }}>E. Way Bill No:- </span><span style={val}>{c.eway_bill_no}</span></div>
      <div style={{ ...row, borderBottom: 0, flex: 1 }}><span style={{ fontWeight: 700 }}>EWAY BILL VALID TILL:- </span><span style={val}>{formatDate(c.eway_bill_valid_till)}</span></div>
    </div>
  );
}

function Goods({ c, sig }) {
  const b = BORDER;
  const td = { borderTop: b, borderRight: b, borderBottom: b, borderLeft: b, padding: "3px 6px", verticalAlign: "top" };
  const th = { ...td, textAlign: "center", fontWeight: 700 };
  const lbl = { fontWeight: 700 };

  return (
    <table style={{ width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "6%" }}/>
        <col style={{ width: "8%" }}/>
        <col style={{ width: "28%" }}/>
        <col style={{ width: "9%" }}/>
        <col style={{ width: "9%" }}/>
        <col style={{ width: "12%" }}/>
        <col style={{ width: "13%" }}/>
        <col style={{ width: "15%" }}/>
      </colgroup>
      <tbody>
        <tr>
          <td style={th}>NO. OF<br/>PKGS</td>
          <td style={th}>METHOD<br/>OF PKNG</td>
          <td style={th}>Contents (Said to Contain)</td>
          <td colSpan={2} style={{ ...th, padding: 0 }}>
            <div style={{ padding: "3px 6px", borderBottom: b }}>Weight</div>
            <div style={{ display: "flex", fontWeight: 400 }}>
              <div style={{ flex: 1, textAlign: "center", padding: "2px 4px", borderRight: b }}>Actual Kg.</div>
              <div style={{ flex: 1, textAlign: "center", padding: "2px 4px" }}>Charged Kg.</div>
            </div>
          </td>
          <td style={th}>Charged</td>
          <td style={th}>Freight<br/><span style={{ fontWeight: 400 }}>To Pay/Paid/To Be Billed</span></td>
          <td style={th}>SIGN&nbsp;&nbsp;OF BOOKING<br/>CLERK</td>
        </tr>
        {/* Row 1 */}
        <tr>
          <td rowSpan={2} style={{ ...td, textAlign: "center", verticalAlign: "middle", fontWeight: 800, fontSize: 15.5 }}>{c.no_pkgs}</td>
          <td rowSpan={2} style={{ ...td, textAlign: "center", verticalAlign: "middle", fontWeight: 800, fontSize: 15.5 }}>{c.method_of_pkg}</td>
          <td rowSpan={6} style={{ ...td, whiteSpace: "pre-line", verticalAlign: "top", fontWeight: 700, fontSize: 15.5 }}>{c.contents}</td>
          <td style={{ ...td, textAlign: "center", fontWeight: 700, fontSize: 15.5 }}>{c.actual_weight}</td>
          <td style={{ ...td, textAlign: "center", fontWeight: 700, fontSize: 15.5 }}>{c.charged_weight}</td>
          <td style={td}>Per KG.</td>
          <td style={{ ...td, textAlign: "right", fontWeight: 700, fontSize: 15.5 }}>{c.per_kg || "-"}</td>
          <td rowSpan={8} style={{ ...td, verticalAlign: "middle", textAlign: "center", backgroundColor: "#ffffff", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
            {sig && sig.has_signature ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, backgroundColor: "#ffffff", isolation: "isolate", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                <img src={`${API_BASE}/auth/users/${sig.user_id}/signature`} alt="Signature" style={{ width: "100%", objectFit: "contain", backgroundColor: "#ffffff", filter: "contrast(150%) grayscale(100%)", mixBlendMode: "darken", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} data-testid="cn-signature-img"/>
                {sig.name && <div style={{ fontWeight: 700, fontSize: 9 }}>{sig.name}</div>}
                <div style={{ fontSize: 8 }}>Authorised Signatory</div>
              </div>
            ) : (
              <span>&nbsp;</span>
            )}
          </td>
        </tr>
        <tr>
          <td colSpan={2} style={{ ...td, textAlign: "center", ...lbl }}>Value declared</td>
          <td style={td}>Hamail</td>
          <td style={{ ...td, textAlign: "right", fontWeight: 700, fontSize: 15.5 }}>{c.hamail || "-"}</td>
        </tr>
        <tr>
          <td rowSpan={2} style={{ ...td, textAlign: "center", verticalAlign: "middle", fontWeight: 800, fontSize: 15.5 }}>{c.no_pkgs}</td>
          <td rowSpan={2} style={{ ...td, textAlign: "center", verticalAlign: "middle", fontWeight: 800, fontSize: 15.5 }}>{c.method_of_pkg}</td>
          <td colSpan={2} style={{ ...td, textAlign: "center", fontWeight: 700, fontSize: 16.5 }}>{c.value_declared}</td>
          <td style={td}>DT. Charge</td>
          <td style={{ ...td, textAlign: "right", fontWeight: 700, fontSize: 15.5 }}>{c.dt_charge || "-"}</td>
        </tr>
        <tr>
          <td colSpan={2} style={{ ...td, textAlign: "center", ...lbl }}>Truck No.</td>
          <td style={td}>Ins/other Ch.</td>
          <td style={{ ...td, textAlign: "right", fontWeight: 700, fontSize: 15.5 }}>{c.ins_other_charge || ""}</td>
        </tr>
        <tr>
          <td colSpan={2} style={td}></td>
          <td colSpan={2} style={{ ...td, textAlign: "center", fontWeight: 800, fontSize: 16.5 }}>{c.truck_no}</td>
          <td style={{ ...td, ...lbl }}>TOTAL</td>
          <td style={{ ...td, textAlign: "right", fontWeight: 800, fontSize: 15.5 }}>{c.total || "---"}</td>
        </tr>
        <tr>
          <td colSpan={6} style={td}>&nbsp;</td>
        </tr>
        <tr>
          <td colSpan={5} rowSpan={2} style={{ ...td, verticalAlign: "top" }}>
            {c.door_delivery && <div style={lbl}>DOOR DELIVERY C.C ATTACHED</div>}
            {(c.driver_name || c.driver_phone) && (
              <div style={lbl}>DRIVER:- {c.driver_name}{c.driver_phone ? `, ${c.driver_phone}` : ""}</div>
            )}
            {c.remarks && <div style={{ marginTop: 4 }}><span style={lbl}>REMARKS:- </span>{c.remarks}</div>}
          </td>
          <td style={{ ...td, ...lbl }}>GRAND TOTAL</td>
          <td style={{ ...td, textAlign: "right", fontWeight: 800, fontSize: 15.5 }}>{c.grand_total || ""}</td>
        </tr>
        <tr>
          <td colSpan={2} style={td}>Seal no. (if applied)- {c.seal_no || "NOT"}</td>
        </tr>
      </tbody>
    </table>
  );
}

function formatDate(d) {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y.slice(2)}`;
  }
  return d;
}
